
import { format, startOfDay, endOfDay, parse } from 'date-fns';
import {
  TaskBehaviours,
} from '@clickster/constant-definitions/dist-esm/entities/automated-task/types/enums';
import { ValidDateFormatsEnum, validDateFormats } from './date-formats';

import type { IOverallRow } from '../interfaces/general/api';
import type { ICampaign } from '@clickster/constant-definitions/dist-esm/entities/campaign/types';
import type { IReport } from '@clickster/constant-definitions/dist-esm/entities/report/types';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import isObject from 'lodash/isObject';
import unset from 'lodash/unset';
// TODO: Migrate getTimeZones to use @clickster/constant-definitions version and remove moment-timezone
import moment from 'moment-timezone';
import { sourceTimezones } from '../components/schedule/utils';
/* import { sourceTimezones, timeZones } from './general/time'; */
import type { ITimeZone } from '@clickster/constant-definitions/dist-esm/types/timezone';
import { cronOptions } from '@clickster/constant-definitions/dist-esm/types/cron-expressions';

type Offer = { distribution: number };
type Option = { distribution: number; offers: Offer[] };
type Flow = { options: Option[] };
export type Path = { flows: Flow[] };

export const ASSETS_HOST = 'https://assets.1010010010.com';

export const generateReportTemplate = (campaignsIds?: string[], timezone?: ITimeZone): IReport => {
  // TODO: fix user timezone
  // const user = Store.get('user') ? setUserTimeZone(Store.get('user')) : undefined;

  return {
    // TODO: Fix types in constant-definitions
    // @ts-ignore
    save: false,
    name: null,
    filters: [
      {
        type: 'include_bot_traffic',
        include_bot_traffic: true,
        orphan_traffic: 'include',
        conversion_mode: 'both',
        offer_clicked: 'both',
      },
      { type: 'income_types', income_types: ['main', 'secondary'] },
      { type: 'TrafficSource', ids: [] },
      { type: 'Campaign', ids: campaignsIds || [] },
      { type: 'Affiliate', ids: [] },
      { type: 'Offer', ids: [] },
      { type: 'TrafficType', ids: ['Real Traffic', 'Suspicious Traffic'] },
    ],
    options: {
      flatten: true,
      totalize: true,
      group_by: [],
      datasource: 'main',
    } as any,
    dates: {
      from: format(startOfDay(new Date()), validDateFormats[0]),
      to: format(endOfDay(new Date()), validDateFormats[0]),
      time_offset: timezone?.value || 0,
      moment_name: (timezone?.moment_name as any) || undefined,
      based_on: 'created_on',
    },
  };
};

export { ValidDateFormatsEnum, validDateFormats } from './date-formats';

export function formatDate(
  dateInput: string | Date,
  outputFormat: ValidDateFormatsEnum = ValidDateFormatsEnum.MM_DD_YYYY,
): string {
  const dateStr = typeof dateInput === 'string' ? dateInput : dateInput.toISOString().split('T')[0];
  const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());

  return format(parsedDate, outputFormat);
}

export const updateAllCampaignOverall = (
  overall: IOverallRow[][],
  campaigns: ICampaign[],
  remoteTrafficSource: any,
) => {
  return overall.map((campaignContent) => {
    return campaignContent.map((campaign) => {
      const sourceCampaign = campaigns.find((sourceCampaign) => sourceCampaign._id === campaign._id);
      const remoteCampaign = get(remoteTrafficSource, ['remote_campaigns'], []).find((remoteCampaign: any) => {
        return String(remoteCampaign.id) === String(get(sourceCampaign, ['remote_campaign', 'id'], null));
      });
      return {
        ...campaign,
        loading_remote_cpc: false,
        real_cost: remoteCampaign ? remoteCampaign.cost : 0,
      };
    });
  });
};

export { };
export const daysSchedule = {
  monday: [{ from: '00:00', to: '24:00' }],
  tuesday: [{ from: '00:00', to: '24:00' }],
  wednesday: [{ from: '00:00', to: '24:00' }],
  thursday: [{ from: '00:00', to: '24:00' }],
  friday: [{ from: '00:00', to: '24:00' }],
  saturday: [{ from: '00:00', to: '24:00' }],
  sunday: [{ from: '00:00', to: '24:00' }],
};

interface ErrorDistribution {
  [key: number]: {
    groupDistribution: number;
    offer: { [key: number]: number };
  };
}

export function calculateErrorDistribution(paths: Path[]): ErrorDistribution {
  let result: ErrorDistribution = {};

  if (isObject(paths[0])) {
    paths.forEach((path, pathIndex) => {
      result[pathIndex] = {
        groupDistribution: path.flows[0].options.length === 0 ? 100 : 0,
        offer: {},
      };

      path.flows[0].options.forEach((opt, optIndex) => {
        result[pathIndex].groupDistribution += opt.distribution;
        result[pathIndex].offer[optIndex] = 0;

        opt.offers.forEach((offer) => {
          result[pathIndex].offer[optIndex] += offer.distribution || 0;
        });
      });
    });
  }

  return result;
}

export const validatePaths = (paths: Path[]) => {
  const errorDistribution = calculateErrorDistribution(paths);

  const errorArray = Object.values(errorDistribution);

  if (
    errorArray.some((x) => {
      return x?.groupDistribution !== 100 || Object.values(x.offer).some((count) => count !== 100);
    })
  ) {
    throw new Error(
      'The sum of all the traffic distributions should be equal to 100. The traffic distribution should not be a decimal number.',
    );
  }
};

export const cleanNickname = (campaign: ICampaign) => {
  if (campaign.tracking_domain.indexOf('clickster.io') > -1) {
    unset(campaign, 'nickname');
  }
  return campaign;
};

export const formatValidationErrors = (errors: any): string[] => {
  const formattedErrors: string[] = [];

  const extractErrors = (obj: any, path: string[] = []) => {
    if (!obj) return;

    Object.entries(obj).forEach(([key, value]: [string, any]) => {
      if (value?.message) {
        formattedErrors.push(value.message);
      } else if (typeof value === 'object') {
        extractErrors(value, [...path, key]);
      }
    });
  };

  extractErrors(errors);
  return formattedErrors;
};

export function getTimeZones() {
  const now = moment();
  // let displayValue
  const timezonesInstance: any[] = cloneDeep(sourceTimezones);

  each(timezonesInstance, (tz) => {
    // displayValue = now.tz(tz.moment_name).format('Z')
    tz.isDST = moment.tz(now.format(), tz.moment_name).isDST();
    tz.value = tz.value;
    if (tz.isDST) {
      tz.default_value = tz.value;
      tz.value -= 1;
      // displayValue = displayValue.replace(Math.abs(tz.default_value), tz.value)
      tz.text = `${tz.text.split('GMT')[0]}GMT`;
      tz.text = tz.text.replace(/\+\-/, '');
      tz.text += tz.value > 0 ? '-' : '+';
      tz.text += Math.abs(tz.value).toFixed(2).replace('.', ':');
      tz.text += ' DST';
    }
  });

  return timezonesInstance;
}

export const getDateState = (timezonesWithDST: any[]) => {
  let time_offset;
  // TODO: get this from the user:
  const moment_name = 'EST5EDT';

  if (moment_name) {
    const timezone = timezonesWithDST.find((tz) => tz.moment_name === moment_name);
    time_offset = timezone ? timezone.value : undefined;
  }

  return {
    from: format(new Date(), 'MM/dd/yyyy'),
    to: format(new Date(), 'MM/dd/yyyy'),
    time_offset,
    moment_name,
  };
};

export const createDraftAutomationTemplate = ({
  userId,
  campaignId,
  name = 'Allow List Blocker (Draft)',
  groupName = 'WHITELIST',
}: {
  userId: string;
  campaignId: string;
  name?: string;
  groupName?: 'WHITELIST' | 'BUDGET_AUTOMATION' | 'BLOCKPLACEMENTS' | 'BIDADJUSTMENTS' | 'BLOCK_PLACEMENTS' | 'PAUSE_PLACEMENTS' | 'PAUSE_ADS';
}) => ({
  behavior: {
    type: TaskBehaviours.notifyAndRun,
    notifyTo: [],
  },
  cron: cronOptions[0].value as typeof cronOptions[number]['value'],
  enabled: false,
  name,
  type: groupName,
  group: {
    _id: `${campaignId}`,
    name: groupName,
  },
  configuration: {
    protection: {
      dateRange: {
        includeToday: true,
      },
      enabled: false,
      filters: [],
    },
    dateRange: {
      includeToday: false,
    },
    roi: {
      enableMinMax: true,
    },
    limits: {
      sortDirection: 'DESC' as const,
    },
    campaigns: [],
    trafficSources: [],
    placements: [],
    affectedPlacements: [],
    whitelistedPlacements: [],
    lists: [],
    executionResults: [],
    filters: [],
    bid: [],
    budget: [],
    status: [],
  },
  user: userId,
  status: 'DRAFT',
  created_on: new Date(),
});


export const getTextSizeClass = (size: 'xs' | 'sm' | 'base' | 'lg' | 'xl' = 'base') => {
  switch (size) {
    case 'xs':
      return 'text-[10px]';
    case 'sm':
      return 'text-[12px]';
    case 'base':
      return 'text-[14px]';
    case 'lg':
      return 'text-[16px]';
    case 'xl':
      return 'text-[18px]';
  }
};

